import { useMemo, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { Link } from 'react-router';
import type { ColumnDef, SortingState, Updater } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type {
  ExportRefundsCsvQuery,
  RefundFilter,
  RefundSort,
  SortDirection,
  TerminalsForRefundsFilterQuery,
} from '@/gql/graphql';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { formatCurrency, formatDate, formatDateTime, formatShortId } from '@/lib/format';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';
import { cn } from '@/lib/cn';
import { useListHref } from '@/hooks/useListLocation';
import {
  isDefault,
  sortingFromUrl,
  sortingToUrl,
  useDebouncedUrlField,
  useUrlState,
} from '@/hooks/useUrlState';
import {
  REFUND_COLUMN_SORT,
  REFUND_METHOD_OPTIONS,
  REFUND_STATUS_OPTIONS,
  REFUNDS_ADVANCED_KEYS,
  REFUNDS_DEFAULT_SORT,
  refundsUrlSchema,
  type RefundMethodValue,
  type RefundStatusValue,
} from './refundsUrlState';

const RefundsDoc = graphql(/* GraphQL */ `
  query Refunds($filter: RefundFilter, $page: Int, $pageSize: Int, $sort: RefundSort) {
    refunds(filter: $filter, page: $page, pageSize: $pageSize, sort: $sort) {
      nodes {
        id
        status
        paymentMethod
        amount
        createdAt
        updatedAt
        recognitionDate
        message
        order {
          id
        }
        tab {
          id
          name
        }
        payment {
          id
        }
        terminal {
          id
          name
        }
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const ExportRefundsCsvDoc = graphql(/* GraphQL */ `
  query ExportRefundsCsv($filter: RefundFilter, $sort: RefundSort) {
    exportRefundsCsv(filter: $filter, sort: $sort) {
      url
      expiresAt
    }
  }
`) as TypedDocumentNode<ExportRefundsCsvQuery>;

const TerminalsForRefundsFilterDoc = graphql(/* GraphQL */ `
  query TerminalsForRefundsFilter {
    terminals(filter: {}, includeInactive: true, page: 1, pageSize: 500) {
      nodes {
        id
        name
      }
    }
  }
`) as TypedDocumentNode<TerminalsForRefundsFilterQuery>;

function multiSelectClassName(): string {
  return cn(
    'block w-full min-h-[11rem] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-xs',
    'focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-200',
    'dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:focus:ring-brand-900/60',
  );
}

const filterSectionTitle =
  'mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
const filterLabel = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200';
const filterHint = 'mt-1.5 text-xs leading-snug text-gray-500 dark:text-gray-400';

function refundStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'refunded':
    case 'success':
      return 'success';
    case 'failure':
    case 'failed':
      return 'error';
    case 'canceled':
      return 'neutral';
    case 'verifying_card':
    case 'refunding':
    case 'processing':
    case 'pending':
    case 'new':
      return 'info';
    default:
      return 'neutral';
  }
}

interface RefundRow {
  id: string;
  status: string;
  paymentMethod: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
  recognitionDate?: string | null;
  message?: string | null;
  order?: { id: string } | null;
  tab?: { id: string; name: string } | null;
  payment?: { id: string } | null;
  terminal?: { id: string; name: string } | null;
}

/**
 * Read-only refunds list: filters map to GraphQL `RefundFilter`; sort is server-driven.
 */
export function RefundsListPage() {
  const apollo = useApolloClient();
  const { state: url, patch, setFilters, reset } = useUrlState(refundsUrlSchema);
  const listHref = useListHref();
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));
  const [amountMin, setAmountMin] = useDebouncedUrlField(url.amountMin, (next) => setFilters({ amountMin: next }));
  const [amountMax, setAmountMax] = useDebouncedUrlField(url.amountMax, (next) => setFilters({ amountMax: next }));
  // The Filters panel opens on mount when a filter inside it is set (a reload, Back, an inbound link).
  const [filtersOpen] = useState(() => REFUNDS_ADVANCED_KEYS.some((key) => !isDefault(refundsUrlSchema, url, key)));
  const sorting = useMemo(() => sortingFromUrl(url), [url]);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, REFUNDS_DEFAULT_SORT));
  };

  const { data: terminalsData } = useQuery(TerminalsForRefundsFilterDoc);

  const filter: RefundFilter = useMemo(() => {
    const out: RefundFilter = {};
    const trimmed = url.search.trim();
    if (trimmed) {
      out.search = trimmed;
    }
    if (url.status) {
      out.status = [url.status];
    }
    if (url.method) {
      out.paymentMethod = [url.method];
    }
    const rf = url.from ? `${url.from}T00:00:00` : null;
    const rt = url.to ? `${url.to}T23:59:59` : null;
    if (rf || rt) {
      out.recognitionDate = { from: rf, to: rt };
    }
    if (url.amountMin !== '' || url.amountMax !== '') {
      out.amount = {
        min: url.amountMin === '' ? null : Number(url.amountMin),
        max: url.amountMax === '' ? null : Number(url.amountMax),
      };
    }
    if (url.terminals.length > 0) {
      out.terminalIds = [...url.terminals];
    }
    return out;
  }, [url]);

  const gqlSort = useMemo(
    (): RefundSort => ({
      field: REFUND_COLUMN_SORT[url.sort],
      direction: (url.dir === 'asc' ? 'ASC' : 'DESC') as SortDirection,
    }),
    [url.sort, url.dir],
  );

  const { data, previousData, loading } = useQuery(RefundsDoc, {
    variables: { filter, page: url.page, pageSize: url.size, sort: gqlSort },
  });
  const refundsData = data ?? previousData;

  const rows: RefundRow[] = useMemo(
    () => refundsData?.refunds.nodes ?? [],
    [refundsData],
  );
  const pageInfo = refundsData?.refunds.pageInfo;
  const terminalNodes = terminalsData?.terminals.nodes ?? [];

  const columns = useMemo<ColumnDef<RefundRow>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: 'Refund',
        cell: ({ row }) => <span className="font-medium">#{formatShortId(row.original.id)}</span>,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge tone={refundStatusTone(row.original.status)}>{row.original.status}</Badge>
        ),
      },
      {
        id: 'method',
        accessorKey: 'paymentMethod',
        header: 'Method',
        cell: ({ row }) => row.original.paymentMethod,
      },
      {
        id: 'amount',
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }) => formatCurrency(row.original.amount),
      },
      {
        id: 'payment',
        accessorFn: (row) => row.payment?.id ?? '',
        header: 'Payment',
        cell: ({ row }) =>
          row.original.payment ? (
            <span className="font-medium text-gray-800 dark:text-gray-200">
              #{formatShortId(row.original.payment.id)}
            </span>
          ) : (
            '—'
          ),
      },
      {
        id: 'order',
        accessorFn: (row) => row.order?.id ?? '',
        header: 'Order',
        cell: ({ row }) =>
          row.original.order ? (
            <Link to={`/orders/${row.original.order.id}`} state={{ from: listHref }} className="text-brand hover:underline">
              #{formatShortId(row.original.order.id)}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'tab',
        accessorFn: (row) => row.tab?.name ?? row.tab?.id ?? '',
        header: 'Tab',
        cell: ({ row }) =>
          row.original.tab ? (
            <Link to={`/tabs/${row.original.tab.id}`} state={{ from: listHref }} className="text-brand hover:underline">
              {row.original.tab.name || `#${formatShortId(row.original.tab.id)}`}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'terminal',
        accessorFn: (row) => row.terminal?.name ?? '',
        header: 'Terminal',
        cell: ({ row }) => row.original.terminal?.name ?? '—',
      },
      {
        id: 'recognitionDate',
        accessorFn: (row) => row.recognitionDate ?? '',
        header: 'Recognition',
        cell: ({ row }) => formatDate(row.original.recognitionDate ?? undefined),
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
    ],
    [listHref],
  );

  const clearAllFilters = () => reset();

  const handleExport = async () => {
    const { data: exportData } = await apollo.query({
      query: ExportRefundsCsvDoc,
      variables: { filter, sort: gqlSort },
      fetchPolicy: 'network-only',
    });
    if (exportData?.exportRefundsCsv?.url) {
      await downloadAuthenticatedCsv(exportData.exportRefundsCsv.url);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Refunds</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Every refund booked against a payment, with links back to the originating order or tab.
        </p>
      </div>

      <FilterBar
        defaultOpen={filtersOpen}
        search={{
          id: 'admin.refunds.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Search by id or payment method…',
        }}
        primary={
          <button
            type="button"
            id="admin.refunds.reset"
            onClick={clearAllFilters}
            className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-xs transition-colors hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Reset all filters
          </button>
        }
        advanced={
          <div className="w-full min-w-0 space-y-10">
            <section className="min-w-0" aria-labelledby="refunds-filters-basics-heading">
              <h3 id="refunds-filters-basics-heading" className={filterSectionTitle}>
                Status, method and recognition date
              </h3>
              <div className="grid w-full grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                <label className="min-w-0 lg:col-span-2">
                  <span className={filterLabel}>Status</span>
                  <Select
                    className="w-full min-w-0"
                    id="admin.refunds.status"
                    value={url.status}
                    onChange={(e) => setFilters({ status: e.target.value as RefundStatusValue | '' })}
                    options={REFUND_STATUS_OPTIONS}
                  />
                </label>
                <label className="min-w-0 lg:col-span-2">
                  <span className={filterLabel}>Payment method</span>
                  <Select
                    className="w-full min-w-0"
                    id="admin.refunds.method"
                    value={url.method}
                    onChange={(e) => setFilters({ method: e.target.value as RefundMethodValue | '' })}
                    options={REFUND_METHOD_OPTIONS}
                  />
                </label>
                <label className="min-w-0 lg:col-span-8">
                  <span className={filterLabel}>Recognition date (from → to)</span>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                    <Input
                      type="date"
                      value={url.from}
                      onChange={(e) => setFilters({ from: e.target.value })}
                      className="min-h-[2.5rem] w-full min-w-0 flex-1"
                    />
                    <span
                      className="hidden shrink-0 self-center text-sm text-gray-400 sm:inline dark:text-gray-500"
                      aria-hidden
                    >
                      →
                    </span>
                    <Input
                      type="date"
                      value={url.to}
                      onChange={(e) => setFilters({ to: e.target.value })}
                      className="min-h-[2.5rem] w-full min-w-0 flex-1"
                    />
                  </div>
                  <span className={filterHint}>
                    Uses each refund&apos;s recognition (business) date, not the created timestamp.
                  </span>
                </label>
              </div>
            </section>

            <section
              className="min-w-0 border-t border-gray-200/90 pt-8 dark:border-white/10"
              aria-labelledby="refunds-filters-amount-heading"
            >
              <h3 id="refunds-filters-amount-heading" className={filterSectionTitle}>
                Amount
              </h3>
              <div className="grid w-full max-w-xl grid-cols-2 gap-x-6 gap-y-4">
                <label className="min-w-0">
                  <span className={filterLabel}>Amount min</span>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={amountMin}
                    onChange={(e) => setAmountMin(e.target.value)}
                    placeholder="Any"
                    className="w-full min-w-0"
                  />
                </label>
                <label className="min-w-0">
                  <span className={filterLabel}>Amount max</span>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={amountMax}
                    onChange={(e) => setAmountMax(e.target.value)}
                    placeholder="Any"
                    className="w-full min-w-0"
                  />
                </label>
              </div>
            </section>

            <section
              className="min-w-0 border-t border-gray-200/90 pt-8 dark:border-white/10"
              aria-labelledby="refunds-filters-terminal-heading"
            >
              <h3 id="refunds-filters-terminal-heading" className={filterSectionTitle}>
                Terminal
              </h3>
              <label className="flex max-w-xl min-w-0 flex-col">
                <span className={filterLabel}>Terminal</span>
                <select
                  multiple
                  className={multiSelectClassName()}
                  value={url.terminals}
                  onChange={(e) =>
                    setFilters({ terminals: Array.from(e.target.selectedOptions, (opt) => opt.value) })
                  }
                  aria-label="Filter by terminal"
                >
                  {terminalNodes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <span className={filterHint}>
                  Cmd/Ctrl-click to select several. Requires permission to list terminals.
                </span>
              </label>
            </section>
          </div>
        }
      />

      <DataGrid<RefundRow>
        data={rows}
        columns={columns}
        loading={loading && !refundsData}
        rowId={(row) => row.id}
        onExport={handleExport}
        sorting={{ state: sorting, onChange: handleSortingChange }}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />
    </div>
  );
}
